import { z } from 'zod';

/**
 * -----------------------------------------------------------------------------
 * Shared (Public) Environment Variables
 * -----------------------------------------------------------------------------
 * Available on both the server and the client.
 */
const sharedSchema = z.object({
  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------
  NEXT_PUBLIC_API_BASE_URL: z.url(),

  // ---------------------------------------------------------------------------
  // Application
  // ---------------------------------------------------------------------------
  NEXT_PUBLIC_APP_ENV: z.enum(['local', 'development', 'staging', 'production']),

  NEXT_PUBLIC_APP_VERSION: z.string().default('0.0.0'),

  // ---------------------------------------------------------------------------
  // Assets
  // ---------------------------------------------------------------------------
  NEXT_PUBLIC_ASSETS_BASE_URL: z.url().optional(),

  // ---------------------------------------------------------------------------
  // Logger
  // ---------------------------------------------------------------------------
  NEXT_PUBLIC_LOG_ENABLED: z.stringbool().default(true),

  NEXT_PUBLIC_LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('debug'),

  NEXT_PUBLIC_LOG_PREFIX: z.string().default('APP'),

  // ---------------------------------------------------------------------------
  // Notifications hub
  // ---------------------------------------------------------------------------
  // Emergency switch for the live hub connection, read at runtime, so an App Service setting turns
  // it off without a rebuild. Off has a cost: the connection is also the session's lock lease (the
  // API releases a session's record locks 30 s after its last connection drops), so with the hub
  // off a lock left behind by a closed tab is never released that way. Off tokens: false, off, 0,
  // no, disabled; an empty or unknown value stops the server at startup rather than guess.
  NEXT_PUBLIC_NOTIFICATIONS_ENABLED: z.stringbool().default(true),

  // Path of the API's SignalR notifications hub, relative to the API ORIGIN (not under /api) — must
  // equal the API's `Notifications:HubPath`. The browser never calls the API host for it: the auth
  // cookie is issued on THIS origin by login.action.ts, so src/proxy.ts routes `<this origin><path>`
  // to `<API origin><path>` and the cookie travels. Read by the proxy and by NotificationsProvider.
  // Must be an absolute path below the root; a trailing slash is dropped (Next's own trailing-slash
  // redirect runs before the proxy, so a request never arrives with one and `/hubs/x/` would never
  // match). The default matches the API's own default.
  NEXT_PUBLIC_NOTIFICATIONS_HUB_PATH: z
    .string()
    .regex(/^\/[^/?#]/, 'must be an absolute path below the root, e.g. /hubs/notifications')
    .transform((hubPath) => hubPath.replace(/\/+$/, ''))
    .default('/hubs/notifications'),

  // Hides the hub debug panel on local (FEATURES.enableHubDebugPanel caps it there); it never shows
  // it elsewhere. Off tokens as above.
  NEXT_PUBLIC_HUB_DEBUG_PANEL: z.stringbool().default(true),

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------
  // The client-side cascade re-trigger after a server value write (docs/EVENTS.md Q11), read at
  // runtime, so an App Service setting turns it off without a rebuild. On by default: if the engine
  // already cascades, the cost is one redundant, idempotent kind-3 per written flagged field; if it
  // does not and this stayed off, the Country → State → City cascade (FSR-DEP-022) would silently
  // break. Off tokens: false, off, 0, no, disabled.
  NEXT_PUBLIC_EVENTS_CASCADE: z.stringbool().default(true),

  // ---------------------------------------------------------------------------
  // Google Maps
  // ---------------------------------------------------------------------------
  // Browser key — loads the Maps JavaScript API client-side, and powers the location search box's
  // live Places predictions. Restrict in Google Cloud Console: HTTP referrers; API restrictions:
  // Maps JavaScript API **and Places API (New)**. Without Places API the map still works and only
  // the suggestions dropdown degrades (see PlaceSearchField / use-place-autocomplete.ts).
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string().optional(),

  // A Google Cloud "Map ID" (cloud-based map style). REQUIRED by `<AdvancedMarker>` — every marker
  // in this app now uses it (ProximityMap's repairer pins as styled DOM nodes; GeographicalMap's
  // single location pin, migrated off the classic `google.maps.Marker` after its Feb 2024
  // deprecation). Without a map id, Advanced Markers silently fall back to default pins and log a
  // console warning — hence the DEMO_MAP_ID fallback both components fall back to when this is unset.
  NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID: z.string().optional(),
});

/**
 * -----------------------------------------------------------------------------
 * Server-only Environment Variables
 * -----------------------------------------------------------------------------
 */
const serverSchema = sharedSchema.extend({
  // Server key — used only from Server Actions (geocode.action.ts) to resolve lat/lng <-> address.
  // Never prefix this with NEXT_PUBLIC_ — it must never reach the browser bundle. Restrict in
  // Google Cloud Console: IP addresses; API restriction: Geocoding API (+ reserved for Maps Static
  // API, not yet used). Optional so the app still builds/runs without it — the map degrades to
  // manual lat/lng/address entry when unset.
  GOOGLE_MAPS_SERVER_API_KEY: z.string().optional(),
});

const clientSchema = sharedSchema;

export type ServerEnv = z.infer<typeof serverSchema>;
export type ClientEnv = z.infer<typeof clientSchema>;

const isServer = typeof window === 'undefined';

/**
 * One artifact is built once and deployed to every client/environment, and each App Service
 * supplies its own values as app settings. So nothing here may be fixed at build time:
 * - `process.env` is only ever read as a whole object. Next inlines a literal
 *   `process.env.NEXT_PUBLIC_X` at build time, on the server as well as in the browser.
 * - Values are parsed on first access, not at import, so `next build` runs with no env set.
 * - The browser reads the values the root layout serialises into the page (`RUNTIME_ENV_ID`).
 */
export const RUNTIME_ENV_ID = '__RUNTIME_ENV__';

let serverCache: ServerEnv | undefined;
let clientCache: ClientEnv | undefined;

export function getServerEnv(): ServerEnv {
  return (serverCache ??= serverSchema.parse(process.env));
}

export function getClientEnv(): ClientEnv {
  return (clientCache ??= isServer ? clientSchema.parse(process.env) : readBrowserEnv());
}

function readBrowserEnv(): ClientEnv {
  const json = document.getElementById(RUNTIME_ENV_ID)?.textContent;

  if (json) return JSON.parse(json) as ClientEnv;

  // No injected element means a test (jsdom). Most tests set no env, so read it unvalidated,
  // as the browser always did, rather than throw.
  const result = clientSchema.safeParse(process.env);
  return result.success ? result.data : (process.env as unknown as ClientEnv);
}

function lazy<T extends object>(load: () => T): T {
  return new Proxy({} as T, { get: (_, key) => Reflect.get(load(), key) });
}

/**
 * Server Environment
 *
 * Validated at server start by `src/instrumentation.ts`.
 */
export const serverEnv: ServerEnv = lazy(getServerEnv);

/**
 * Client Environment
 *
 * Public values only. The server renders them into the page at request time.
 */
export const clientEnv: ClientEnv = lazy(getClientEnv);
